import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { XIcon } from 'lucide-react';
import { categoryLabels, events, type TimelineEvent } from './gameTimelineData';
import { categoryChipClasses, categoryIcon, teamAccent } from './gameTimelineIcons';
const MINUTES = Array.from({
  length: 40
}, (_, i) => i + 1);
const POPOVER_WIDTH = 320;
interface ActiveMarker {
  event: TimelineEvent;
  element: HTMLButtonElement;
  pinned: boolean;
}
interface EventPopoverProps {
  marker: ActiveMarker;
  onClose: () => void;
}
const EventPopover = ({
  marker,
  onClose
}: EventPopoverProps) => {
  const {
    event,
    element,
    pinned
  } = marker;
  const [position, setPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const updatePosition = useCallback(() => {
    const rect = element.getBoundingClientRect();
    const spaceRight = window.innerWidth - rect.right;
    const left = spaceRight > POPOVER_WIDTH + 24 ? rect.right + 10 : Math.max(8, rect.left - POPOVER_WIDTH - 10);
    const top = Math.min(Math.max(8, rect.top + rect.height / 2 - 48), Math.max(8, window.innerHeight - 160));
    setPosition({
      top,
      left
    });
  }, [element]);
  useLayoutEffect(() => {
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [updatePosition]);
  if (!position) return null;
  return <div role={pinned ? 'dialog' : 'tooltip'} aria-label={`${event.event} details`} style={{
    position: 'fixed',
    top: position.top,
    left: position.left,
    width: POPOVER_WIDTH,
    zIndex: 50
  }} className={`bg-white border border-gray-200 border-l-4 rounded-lg shadow-lg p-3 ${teamAccent(event.team).border}`} data-id="element-413" data-mp="21rixgm4mlk4e">
      {pinned && <button type="button" onClick={onClose} aria-label="Close event details" className="absolute top-1.5 right-1.5 p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100" data-id="element-414" data-mp="34emn2a0071hb">
          <XIcon className="w-3.5 h-3.5" data-id="element-415" data-mp="3e590bxk8zfwu" />
        </button>}
      <div className="flex items-start gap-3" data-id="element-416" data-mp="3i5jdbu6gj6l7">
        <span className={`flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full border ${categoryChipClasses[event.category]}`} data-id="element-417" data-mp="3i59oi9x5f5h3">
          {categoryIcon(event.category, 'w-4 h-4')}
        </span>
        <div className="min-w-0 pr-4" data-id="element-418" data-mp="2ug6vy70z7s7t">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1" data-id="element-419" data-mp="2uf5ixx0egvng">
            <span className="text-sm font-medium text-gray-500" data-id="element-420" data-mp="2zf1qu7c7x78a">
              {event.quarter} · {event.time}
            </span>
            <span className={`text-[11px] uppercase tracking-wide px-2 py-0.5 rounded-full border ${categoryChipClasses[event.category]}`} data-id="element-421" data-mp="9oy36a23a7gi">
              {categoryLabels[event.category]}
            </span>
            {event.score && <span className="text-xs font-semibold text-gray-500" data-id="element-422" data-mp="ju6d29tipe8h">
                {event.score}
              </span>}
          </div>
          <p className="font-medium text-gray-900 mt-1" data-id="element-423" data-mp="f1mypan0af2m">
            {event.event}
            {event.player && ` — ${event.player}`}
          </p>
          <p className="text-sm text-gray-600 mt-0.5" data-id="element-424" data-mp="6289sfmhmck8">{event.detail}</p>
        </div>
      </div>
    </div>;
};
export const MinutesTable = () => {
  const [active, setActive] = useState<ActiveMarker | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const clear = () => setActive(null);
  const handleEnter = (event: TimelineEvent, element: HTMLButtonElement) => {
    setActive(prev => prev?.pinned ? prev : {
      event,
      element,
      pinned: false
    });
  };
  const handleLeave = (event: TimelineEvent) => {
    setActive(prev => prev && !prev.pinned && prev.event === event ? null : prev);
  };
  const handleClick = (event: TimelineEvent, element: HTMLButtonElement) => {
    setActive(prev => prev?.pinned && prev.event === event ? null : {
      event,
      element,
      pinned: true
    });
  };

  // Close pinned popover on outside click or Escape
  useEffect(() => {
    if (!active?.pinned) return;
    const onPointerDown = (nativeEvent: MouseEvent) => {
      const target = nativeEvent.target as Node;
      if (containerRef.current?.contains(target) || target instanceof Element && target.closest('[role="dialog"]')) {
        return;
      }
      clear();
    };
    const onKeyDown = (nativeEvent: KeyboardEvent) => {
      if (nativeEvent.key === 'Escape') clear();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [active]);
  return <section aria-label="Minute by minute events" className="mb-6" data-id="element-425" data-mp="d73y9l874i6g">
      <div className="flex items-baseline justify-between mb-2" data-id="element-426" data-mp="88sofahwi7o8">
        <h3 className="text-sm font-semibold text-gray-900" data-id="element-427" data-mp="8an5uetyrhnk">
          Minutes overview
        </h3>
        <p className="text-xs text-gray-500" data-id="element-428" data-mp="m9wp5ndgurvr">
          Hover an event for details, click to keep it open
        </p>
      </div>

      <div ref={containerRef} className="overflow-x-auto border border-gray-200 rounded-lg" data-id="element-429" data-mp="w6ri7qqz002n">
        <div className="min-w-max" data-id="element-430" data-mp="r86kpz33ruhw">
          {/* Quarter band */}
          <div className="flex bg-white text-[11px] text-gray-400" data-id="element-431" data-mp="r5u2rlxchk4u">
            {[1, 2, 3, 4].map(quarter => <div key={quarter} className="w-80 py-1 text-center font-medium uppercase tracking-wider border-r border-gray-200 last:border-r-0" data-id="element-432" data-mp="10xq4d6yn3sdt">
                Q{quarter}
              </div>)}
          </div>

          {/* Minute numbers */}
          <div className="flex bg-gray-50 text-xs text-gray-500 border-y border-gray-200" data-id="element-433" data-mp="7l06jgnwpqfb">
            {MINUTES.map(minute => <div key={minute} className={`w-8 py-1.5 text-center font-medium ${minute % 10 === 0 && minute !== 40 ? 'border-r border-gray-300' : ''}`} data-id="element-434" data-mp="ckmiznvwv2n2">
                {minute}
              </div>)}
          </div>

          {/* Stacked events per minute */}
          <div className="flex items-start bg-white" data-id="element-435" data-mp="v99og7i01u16">
            {MINUTES.map(minute => {
            const minuteEvents = events.filter(event => event.minute === minute);
            return <div key={minute} className={`w-8 min-h-[44px] py-1.5 flex flex-col items-center gap-1 ${Math.ceil(minute / 10) % 2 === 0 ? 'bg-gray-50/60' : ''} ${minute % 10 === 0 && minute !== 40 ? 'border-r border-gray-300' : ''}`} data-id="element-436" data-mp="148wmqkffmjq0">
                  {minuteEvents.map(event => {
                const isActive = active?.event === event;
                return <button key={event.id} type="button" onMouseEnter={e => handleEnter(event, e.currentTarget)} onMouseLeave={() => handleLeave(event)} onFocus={e => handleEnter(event, e.currentTarget)} onBlur={() => handleLeave(event)} onClick={e => handleClick(event, e.currentTarget)} aria-expanded={isActive} aria-label={`${event.quarter} ${event.time} — ${categoryLabels[event.category]}${event.player ? `, ${event.player}` : ''}`} className={`inline-flex items-center justify-center w-6 h-6 rounded-full border transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-400 ${categoryChipClasses[event.category]} ${isActive ? 'ring-2 ring-offset-1 ring-gray-400' : ''}`} data-id="element-437" data-mp="2kshkpy9ndnxi">
                        {categoryIcon(event.category, 'w-3.5 h-3.5')}
                      </button>;
              })}
                </div>;
          })}
          </div>
        </div>
      </div>

      {active && <EventPopover marker={active} onClose={clear} data-id="element-438" data-mp="5143gf8gvdr5" />}
    </section>;
};
export default MinutesTable;